import { useEffect } from 'react'
import { countEffectiveChars, zStringEffectiveLimit } from '@/lib/text/count'
import { cn } from '@/lib/utils'
import { KeywordEditor } from '@/features/editor/components/knowledge/keyword-editor'
import type {
  KnowledgeEntry,
  KnowledgeKind,
} from '@/features/editor/types/knowledge.types'

type EntryEditorProps = {
  entry: KnowledgeEntry | null
  kind: KnowledgeKind
  onPatch: (id: string, patch: Partial<KnowledgeEntry>) => void
}

export function EntryEditor({ entry, kind, onPatch }: EntryEditorProps) {
  const titlePlaceholder =
    kind === 'worldInfo' ? '地点/组织/物品/概念/文化/规则' : '角色姓名'
  const contentPlaceholder =
    kind === 'worldInfo'
      ? '这里存放具体的“事实”，是AI构筑虚拟世界的唯一依据。'
      : '这里存放具体的“人设”，AI会像查资料一样，按需从这里抽调档案来构建具体人物。'

  useEffect(() => {
    if (!entry) return
    const patch: Partial<KnowledgeEntry> = {}

    if (entry.retrievalMethod !== 'prompt') {
      patch.retrievalMethod = 'prompt'
    }

    if (entry.keywordOp !== 'or') {
      patch.keywordOp = 'or'
    }

    if (Object.keys(patch).length > 0) {
      onPatch(entry.id, patch)
    }
  }, [entry, onPatch])

  const limit = kind === 'worldInfo' ? 1800 : 2500
  const contentCount = entry ? countEffectiveChars(entry.content) : 0

  return (
    <div className='border-border bg-background flex h-full min-h-0 flex-col rounded-2xl border'>
      {/* ✅ Header 固定（纯编辑，不放启用/删除） */}
      <div className='border-border shrink-0 border-b px-4 py-3'>
        <div className='text-sm font-medium'>条目编辑</div>
        <div className='text-muted-foreground text-xs'>
          修改会进入草稿，保存后才写入后端。
        </div>
      </div>

      {!entry ? (
        <div className='text-muted-foreground flex flex-1 items-center justify-center p-4 text-sm'>
          请选择左侧一个条目。
        </div>
      ) : (
        // ✅ Body 可滚动
        <div className='min-h-0 flex-1 overflow-auto p-4'>
          <div className='space-y-4'>
            <div className='space-y-1'>
              <div className='text-muted-foreground text-xs font-medium'>
                标题
              </div>
              <input
                value={entry.title}
                onChange={(e) => onPatch(entry.id, { title: e.target.value })}
                placeholder={titlePlaceholder}
                className='bg-background border-border placeholder:text-muted-foreground hover:bg-background/80 focus-visible:border-ring focus-visible:ring-ring/30 w-full rounded-lg border px-3 py-2 text-sm transition outline-none focus-visible:ring-2'
              />
            </div>

            <div className='space-y-1'>
              <div className='text-muted-foreground text-xs font-medium'>
                档案内容
              </div>
              <textarea
                value={entry.content}
                onChange={(e) =>
                  onPatch(entry.id, {
                    content: e.target.value,
                  })
                }
                placeholder={contentPlaceholder}
                className={cn(
                  'bg-background border-border placeholder:text-muted-foreground hover:bg-background/80 focus-visible:border-ring focus-visible:ring-ring/30 min-h-[180px] w-full resize-y rounded-lg border px-3 py-2 text-sm transition outline-none focus-visible:ring-2',
                  !zStringEffectiveLimit(limit).safeParse(entry.content)
                    .success &&
                    'border-destructive focus-visible:ring-destructive/30'
                )}
              />
              <div className='text-muted-foreground text-right text-xs'>
                {contentCount > limit ? (
                  <span className='text-destructive font-bold'>
                    字符：{contentCount}/{limit} (超限)
                  </span>
                ) : (
                  `字符：${contentCount}/${limit}`
                )}
              </div>
            </div>

            <div className='bg-muted/20 border-border space-y-3 rounded-xl border p-3'>
              <KeywordEditor
                keywords={entry.keywords ?? []}
                onChange={(next) => onPatch(entry.id, { keywords: next })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
